import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { all_routes } from "../../router/all_routes"
import TooltipOption from "../../../core/common/tooltipOption"
import Table from "../../../core/common/dataTable/index"
import type { ColumnsType } from "antd/es/table"
import ImageWithBasePath from "../../../core/common/imageWithBasePath"
import { getEmployeeMonthlyReport } from "../../../service/reports"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { toast } from "react-toastify"
import { CLASSES_SORT_OPTIONS } from "../../../core/common/selectoption/selectoption"
import { useSelector } from "react-redux"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { absoluteCampusLogoUrl, campusBrandName } from "../../../utils/campusLogos"

const STATUS_COLORS: Record<string, string> = {
  present: "#10B981",
  absent: "#EF4444",
  late: "#3d5ee1",
  "half-day": "#A855F7",
  leave: "#3B82F6",
  "off-day": "#64748B",
  "not-marked": "#9CA3AF",
}

const STATUS_LABEL: Record<string, string> = {
  present: "Present",
  absent: "Absent",
  late: "Late",
  "half-day": "Half Day",
  leave: "Leave",
  "off-day": "Off Day",
  "not-marked": "Not Marked",
}

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")

// Type definitions
interface DayDetail {
  status?: string
  checkIn?: string
  checkOut?: string
}

interface TableRowData {
  key: string
  originalData: any
  dayDetails: Record<number, DayDetail>
  user: any
  employee: any
  percentage: number
  percentageDisplay: string
  // Working days for this campus this month -- the denominator behind %.
  // Comes straight from the API so the figure the percentage is actually
  // divided by is visible, rather than something the reader has to infer.
  w: number
  p: number
  l: number
  a: number
  h: number
  lv: number
  img: string
  percentClass: string
  [key: string]: any
}

const StaffReport = () => {
  const currentBranch = useSelector(selectCurrentBranch)
  const routes = all_routes
  const [tableData, setTableData] = useState<TableRowData[]>([])

  const getCurrentKarachiMonth = (): string => {
    const now = new Date()
    const karachiDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Karachi" }),
    )
    const year = karachiDate.getFullYear()
    const month = String(karachiDate.getMonth() + 1).padStart(2, "0")
    return `${year}-${month}`
  }

  const [selectedMonth, setSelectedMonth] = useState<string>(
    getCurrentKarachiMonth(),
  )
  const [loading, setLoading] = useState(true)
  const [printLoading, setPrintLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  const [sortBy, setSortBy] = useState(CLASSES_SORT_OPTIONS[0])
  const [searchText, setSearchText] = useState("")

  const [appliedFilters, setAppliedFilters] = useState({
    page: 1,
    limit: 10,
    sortBy: "createdAt",
    sortOrder: "desc" as "asc" | "desc",
  })

  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    limit: 10,
    total: 0,
  })

  const selectedDate = new Date(selectedMonth)
  const currentMonth = selectedDate.getMonth() + 1
  const currentYear = selectedDate.getFullYear()

  const getTodayStr = (): string => {
    const now = new Date()
    const karachiDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Karachi" }),
    )
    const year = karachiDate.getFullYear()
    const month = String(karachiDate.getMonth() + 1).padStart(2, "0")
    const day = String(karachiDate.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const getPercentageClass = (percentage: number): string => {
    if (percentage >= 90) return "text-success"
    if (percentage >= 75) return "text-warning"
    return "text-danger"
  }

  const getStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case "present":
        return "1"
      case "late":
        return "2"
      case "absent":
        return "3"
      case "half-day":
        return "4"
      case "leave":
        return "6"
      case "off-day":
        return "7"
      default:
        return "5"
    }
  }

  const getTagBackgroundColor = (status: string): string =>
    STATUS_COLORS[status?.toLowerCase()] ?? STATUS_COLORS["not-marked"]

  const getTagTextColor = (status: string): string => {
    const s = status?.toLowerCase()
    return s === "late" || s === "not-marked" || s === "" ? "#1a1a1a" : "#fff"
  }

  const normalizeStatus = (rawStatus: any): string => {
    const s = String(rawStatus ?? "")
      .trim()
      .toLowerCase()
    if (s === "1") return "present"
    if (s === "2") return "late"
    if (s === "3") return "absent"
    if (s === "4") return "half-day"
    if (s === "6") return "leave"
    if (s === "7") return "off-day"
    if (s === "half day" || s === "halfday") return "half-day"
    if (s === "off day" || s === "offday" || s === "sunday") return "off-day"
    if (s === "not marked" || s === "notmarked" || s === "unmarked")
      return "not-marked"
    if (s === "") return "not-marked"
    return s
  }

  const formatTime = (value: string | undefined): string => {
    if (!value || typeof value !== "string") return ""
    const trimmed = value.trim()
    if (!trimmed) return ""
    const date = new Date(trimmed)
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-US", {
        timeZone: "Asia/Karachi",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
    }
    const timeMatch = trimmed.match(/(\d{2}:\d{2}:\d{2})/)
    return timeMatch ? timeMatch[1] : trimmed
  }

  const transformEmployeeDataToTableFormat = (
    apiData: any[],
  ): TableRowData[] => {
    if (!apiData || !Array.isArray(apiData)) return []

    const todayStr = getTodayStr()
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate()

    return apiData.map(employee => {
      const attendanceRecords = employee.attendanceRecords || []
      const recordsByDay = new Map()

      attendanceRecords.forEach((record: any) => {
        const date = new Date(record.date)
        recordsByDay.set(date.getDate(), record)
      })

      const apiSummary = employee.summary

      const tableRow: TableRowData = {
        key: employee._id || Math.random().toString(),
        originalData: employee,
        dayDetails: {},
        user: employee.user,
        employee: employee,
        percentage: apiSummary?.attendancePercentage || 0,
        percentageDisplay: `${apiSummary?.attendancePercentage || 0}%`,
        w: apiSummary?.workingDays || 0,
        p:
          (apiSummary?.presentDays || 0) +
          (apiSummary?.lateDays || 0) +
          (apiSummary?.halfDays || 0),
        l: apiSummary?.lateDays || 0,
        a: 0,
        h: apiSummary?.halfDays || 0,
        lv: apiSummary?.leaveDays || 0,
        img:
          employee.user?.profilePicture || "/assets/img/profiles/avatar-01.jpg",
        percentClass: getPercentageClass(apiSummary?.attendancePercentage || 0),
      }

      let computedAbsent = 0

      for (let day = 1; day <= daysInMonth; day++) {
        const dayKey = `d${day.toString().padStart(2, "0")}`
        const dayDate = new Date(currentYear, currentMonth - 1, day)
        const dayDateStr = dayDate.toISOString().split("T")[0]
        const isSunday = dayDate.getDay() === 0
        const isFutureDate = dayDateStr > todayStr

        if (isFutureDate) {
          tableRow[dayKey] = isSunday ? "7" : "5"
          continue
        }

        const dayRecord = recordsByDay.get(day)

        if (dayRecord) {
          let derivedCheckIn: string | undefined
          let derivedCheckOut: string | undefined

          if (dayRecord.inOut && Array.isArray(dayRecord.inOut)) {
            const punches = dayRecord.inOut.slice().reverse()
            const latestIn = punches.find((r: any) => r.type === "in")
            const latestOut = punches.find((r: any) => r.type === "out")
            if (latestIn?.time) derivedCheckIn = latestIn.time
            if (latestOut?.time) derivedCheckOut = latestOut.time
          }

          tableRow.dayDetails[day] = {
            status: dayRecord.status,
            checkIn: derivedCheckIn,
            checkOut: derivedCheckOut,
          }

          const normalized = normalizeStatus(dayRecord.status)
          tableRow[dayKey] = getStatusColor(normalized)
        } else {
          const dailyBreakdown = employee.dailyBreakdown || {}
          const breakdownStatus = dailyBreakdown[day]?.status
          tableRow[dayKey] = breakdownStatus
            ? getStatusColor(normalizeStatus(breakdownStatus))
            : isSunday
              ? "7"
              : "5"
        }

        if (tableRow[dayKey] === "3") computedAbsent += 1
      }

      tableRow.a = computedAbsent
      return tableRow
    })
  }

  const fetchEmployeeReport = async () => {
    try {
      setLoading(true)
      const selectedDateObj = new Date(selectedMonth)
      const response = await getEmployeeMonthlyReport({
        month: selectedDateObj.getMonth() + 1,
        year: selectedDateObj.getFullYear(),
        searchText: searchText,
        sortBy: appliedFilters.sortBy,
        sortOrder: appliedFilters.sortOrder,
        page: appliedFilters.page,
        limit: appliedFilters.limit,
      })

      if (response.success) {
        setTableData(transformEmployeeDataToTableFormat(response.data))
        if (response.pagination) {
          setPaginationData({
            currentPage: response.pagination.currentPage,
            limit: response.pagination.limit,
            total: response.pagination.total,
          })
        }
      } else {
        setTableData([])
      }
    } catch (error) {
      console.error("Error fetching employee report:", error)
      setTableData([])
    } finally {
      setLoading(false)
    }
  }

  const handleMonthChange = (month: string) => setSelectedMonth(month)

  const handleSortSelect = (item: any) => {
    setSortBy(item)
    setAppliedFilters(prev => ({
      ...prev,
      sortBy: item.value.sortBy,
      sortOrder: item.value.sortOrder,
      page: 1,
    }))
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    setAppliedFilters(prev => ({ ...prev, page, limit: pageSize }))
  }

  const handleRefresh = () => fetchEmployeeReport()

  const buildPrintHeaders = () => {
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate()
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    const summaryHeaders = ["S#", "Staff Name", "Emp ID", "Dept", "Designation", "%", "W", "P", "L", "A", "H", "Lv"]
    const dayHeaders: string[] = []
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(currentYear, currentMonth - 1, day)
      dayHeaders.push(`${day.toString().padStart(2, "0")}<br>${dayNames[dayDate.getDay()]}`)
    }
    return { summaryHeaders, dayHeaders, daysInMonth }
  }

  const buildPrintRows = (allRows: TableRowData[], daysInMonth: number) => {
    const statusText: Record<string, string> = {
      "1": "P", "2": "L", "3": "A", "4": "H", "5": "", "6": "Lv", "7": "OFF",
    }
    return allRows.map((item, idx) => {
      const fullName = `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim()
      const nameHtml = fullName.split(" ").filter(Boolean).join("<br>")
      const summary = [
        idx + 1,
        nameHtml,
        item.employee?.employeeID || "",
        item.employee?.department || "",
        item.employee?.designation || "",
        item.percentage,
        item.w, item.p, item.l, item.a, item.h, item.lv,
      ]
      const days: string[] = []
      for (let day = 1; day <= daysInMonth; day++) {
        const dayKey = `d${day.toString().padStart(2, "0")}`
        const code = String(item[dayKey] ?? "5")
        const status = statusText[code] ?? ""
        const detail = item.dayDetails?.[day]
        const inTime = formatTime(detail?.checkIn)
        const outTime = formatTime(detail?.checkOut)
        if (inTime || outTime) {
          const t1 = inTime ? inTime.substring(0, 5).replace(":", "") : ""
          const t2 = outTime ? outTime.substring(0, 5).replace(":", "") : ""
          days.push(`${status}<br>${t1}<br>${t2}`)
        } else {
          days.push(status)
        }
      }
      return [...summary, ...days]
    })
  }

  const printCSS = `
    body { font-family: Arial, sans-serif; font-size: 7px; margin: 0; padding: 0; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ccc; padding: 1px 2px; text-align: center; vertical-align: middle; }
    th { background: #2980b9; color: #fff; white-space: nowrap; font-size: 7px; }
    td { font-size: 7px; }
    .page-break { page-break-after: always; }
    @page { margin: 3mm; }
    .print-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; border-bottom: 1.5px solid #2980b9; padding-bottom: 3px; }
    .school-brand { display: flex; align-items: center; gap: 5px; }
    .school-brand img { height: 34px; object-fit: contain; }
    .school-brand strong { font-size: 13px; letter-spacing: 1px; }
    .report-meta { text-align: center; font-size: 8px; }
    .sheet-info { text-align: right; font-size: 8px; }
  `

  const handlePrintClick = async () => {
    const total = paginationData.total
    if (!total) { toast.warning("No data to print"); return }
    setPrintLoading(true)
    try {
      const selectedDateObj = new Date(selectedMonth)
      const response = await getEmployeeMonthlyReport({
        month: selectedDateObj.getMonth() + 1,
        year: selectedDateObj.getFullYear(),
        searchText,
        sortBy: appliedFilters.sortBy,
        sortOrder: appliedFilters.sortOrder,
        page: 1,
        limit: total,
      })
      if (!response.success || !response.data?.length) {
        toast.warning("No data to print")
        return
      }
      const allRows = transformEmployeeDataToTableFormat(response.data)
      const { summaryHeaders, dayHeaders, daysInMonth } = buildPrintHeaders()
      const headerRow = [...summaryHeaders, ...dayHeaders]
      const bodyRows = buildPrintRows(allRows, daysInMonth)

      const logoUrl = absoluteCampusLogoUrl(currentBranch?.blackLogoUrl)
      const brandName = escapeHtml(campusBrandName(currentBranch))
      const headerHtml = `
        <div class="print-header">
          <div class="school-brand">
            <img src="${logoUrl}" onerror="this.style.display='none'" />
            <strong>${brandName}</strong>
          </div>
          <div class="report-meta">Staff Attendance Sheet ${selectedMonth}</div>
          <div class="sheet-info">&nbsp;</div>
        </div>
      `

      const PAGE_SIZE = 25
      const theadHtml = `<thead><tr>${headerRow.map(h => `<th>${h}</th>`).join("")}</tr></thead>`
      const pages: string[] = []
      for (let i = 0; i < bodyRows.length; i += PAGE_SIZE) {
        const chunk = bodyRows.slice(i, i + PAGE_SIZE)
        pages.push(`<table>${theadHtml}<tbody>${chunk.map(r => `<tr>${r.map(c => `<td>${c ?? ""}</td>`).join("")}</tr>`).join("")}</tbody></table>`)
      }

      const html = `<html><head><title>Staff Attendance Report</title>
        <meta charset="utf-8"/>
        <style>${printCSS}</style></head>
        <body>
          ${headerHtml}
          ${pages.join('<div class="page-break"></div>')}
        </body></html>`

      const win = window.open("", "_blank")
      if (!win) { toast.error("Popup blocked — allow popups and try again"); return }
      win.document.write(html)
      win.document.close()
      win.focus()
      setTimeout(() => { win.print(); win.close() }, 250)
    } catch (e) {
      console.error(e)
      toast.error("Failed to load data for print")
    } finally {
      setPrintLoading(false)
    }
  }

  const handlePrintBlank = () => {
    const { summaryHeaders, dayHeaders } = buildPrintHeaders()
    const headerRow = [...summaryHeaders, ...dayHeaders]
    const emptyRows = Array.from({ length: 30 }, () => Array(headerRow.length).fill(""))

    const logoUrl = absoluteCampusLogoUrl(currentBranch?.blackLogoUrl)
    const brandName = escapeHtml(campusBrandName(currentBranch))
    const headerHtml = `
      <div class="print-header">
        <div class="school-brand">
          <img src="${logoUrl}" onerror="this.style.display='none'" />
          <strong>${brandName}</strong>
        </div>
        <div class="report-meta">Staff Attendance Sheet ${selectedMonth}</div>
        <div class="sheet-info">&nbsp;</div>
      </div>
    `
    const html = `<html><head><title>Blank Attendance Sheet</title>
      <style>${printCSS} td { min-height: 14px; }</style></head>
      <body>
        ${headerHtml}
        <table>
          <thead><tr>${headerRow.map(h => `<th>${h}</th>`).join("")}</tr></thead>
          <tbody>${emptyRows.map(() => `<tr>${headerRow.map(() => `<td>&nbsp;</td>`).join("")}</tr>`).join("")}</tbody>
        </table>
      </body></html>`

    const win = window.open("", "_blank")
    if (!win) { toast.error("Popup blocked — allow popups and try again"); return }
    win.document.write(html)
    win.document.close()
    win.focus()
    setTimeout(() => { win.print(); win.close() }, 250)
  }

  const handleExportPdf = async () => {
    const total = paginationData.total
    if (!total) {
      toast.warning("No data to export")
      return
    }
    setPdfLoading(true)
    try {
      const selectedDateObj = new Date(selectedMonth)
      const monthNum = selectedDateObj.getMonth() + 1
      const yearNum = selectedDateObj.getFullYear()
      const response = await getEmployeeMonthlyReport({
        month: monthNum,
        year: yearNum,
        searchText,
        sortBy: appliedFilters.sortBy,
        sortOrder: appliedFilters.sortOrder,
        page: 1,
        limit: total,
      })
      if (!response.success || !response.data?.length) {
        toast.warning("No data to export")
        return
      }
      const allRows = transformEmployeeDataToTableFormat(response.data)
      const daysInMonth = new Date(yearNum, monthNum, 0).getDate()
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
      const statusText: Record<string, string> = {
        "1": "P",
        "2": "L",
        "3": "A",
        "4": "H",
        "5": "-",
        "6": "Lv",
        "7": "OFF",
      }

      const summaryHeaders = [
        "Staff Name",
        "Emp ID",
        "Dept",
        "Designation",
        "%",
        "W",
        "P",
        "L",
        "A",
        "H",
        "Lv",
      ]
      const dayHeaders: string[] = []
      for (let day = 1; day <= daysInMonth; day++) {
        const dayDate = new Date(yearNum, monthNum - 1, day)
        dayHeaders.push(
          `${day.toString().padStart(2, "0")}(${dayNames[dayDate.getDay()]})`,
        )
      }
      const head = [[...summaryHeaders, ...dayHeaders]]

      const body = allRows.map(item => {
        const summary = [
          `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim(),
          item.employee?.employeeID || "N/A",
          item.employee?.department || "N/A",
          item.employee?.designation || "N/A",
          `${item.percentage}%`,
          item.w,
          item.p,
          item.l,
          item.a,
          item.h,
          item.lv,
        ]
        const days: string[] = []
        for (let day = 1; day <= daysInMonth; day++) {
          const dayKey = `d${day.toString().padStart(2, "0")}`
          const code = String(item[dayKey] ?? "5")
          const status = statusText[code] ?? " - "
          const detail = item.dayDetails?.[day]
          const inTime = formatTime(detail?.checkIn)
          const outTime = formatTime(detail?.checkOut)
          if (inTime || outTime) {
            const t1 = inTime ? inTime.substring(0, 5) : ""
            const t2 = outTime ? outTime.substring(0, 5) : ""
            const timeStr = t1 && t2 ? `${t1} - ${t2}` : t1 || t2
            days.push(`${status} (${timeStr})`)
          } else {
            days.push(status)
          }
        }
        return [...summary, ...days]
      })

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      })
      doc.setFontSize(10)
      doc.text(`Staff Attendance Report — ${selectedMonth}`, 14, 10)
      autoTable(doc, {
        head,
        body,
        startY: 15,
        styles: { fontSize: 4, cellPadding: 1, overflow: "linebreak" },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 5 },
        columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 8 } },
      })
      const monthStr = selectedMonth.substring(0, 7)
      doc.save(`staff_attendance_report_${monthStr}.pdf`)
      toast.success("Exported to PDF successfully")
    } catch (error) {
      console.error("Error exporting to PDF:", error)
      toast.error("Failed to export to PDF")
    } finally {
      setPdfLoading(false)
    }
  }

  const handleExportExcel = async () => {
    const total = paginationData.total
    if (!total) {
      toast.warning("No data to export")
      return
    }
    setExcelLoading(true)
    try {
      const selectedDateObj = new Date(selectedMonth)
      const response = await getEmployeeMonthlyReport({
        month: selectedDateObj.getMonth() + 1,
        year: selectedDateObj.getFullYear(),
        searchText,
        sortBy: appliedFilters.sortBy,
        sortOrder: appliedFilters.sortOrder,
        page: 1,
        limit: total,
      })
      if (!response.success || !response.data?.length) {
        toast.warning("No data to export")
        return
      }
      const allRows = transformEmployeeDataToTableFormat(response.data)
      const selectedDateObj2 = new Date(selectedMonth)
      const monthNum = selectedDateObj2.getMonth() + 1
      const yearNum = selectedDateObj2.getFullYear()
      const daysInMonth = new Date(yearNum, monthNum, 0).getDate()
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
      const statusText: Record<string, string> = {
        "1": "P",
        "2": "L",
        "3": "A",
        "4": "H",
        "5": "-",
        "6": "Lv",
        "7": "OFF",
      }

      const rows = allRows.map(item => {
        const row: Record<string, any> = {
          "Staff Name":
            `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim(),
          "Employee ID": item.employee?.employeeID || "N/A",
          Department: item.employee?.department || "N/A",
          Designation: item.employee?.designation || "N/A",
          "%": `${item.percentage}%`,
          P: item.p,
          L: item.l,
          A: item.a,
          H: item.h,
          Lv: item.lv,
        }
        for (let day = 1; day <= daysInMonth; day++) {
          const dayDate = new Date(yearNum, monthNum - 1, day)
          const dayLabel = `${day.toString().padStart(2, "0")} (${dayNames[dayDate.getDay()]})`
          const dayKey = `d${day.toString().padStart(2, "0")}`
          const code = String(item[dayKey] ?? "5")
          const status = statusText[code] ?? "-"
          const detail = item.dayDetails?.[day]
          const inTime = formatTime(detail?.checkIn)
          const outTime = formatTime(detail?.checkOut)
          if (inTime || outTime) {
            const t1 = inTime ? inTime.substring(0, 5) : ""
            const t2 = outTime ? outTime.substring(0, 5) : ""
            const timeStr = t1 && t2 ? `${t1} - ${t2}` : t1 || t2
            row[dayLabel] = `${status} (${timeStr})`
          } else {
            row[dayLabel] = status
          }
        }
        return row
      })

      const worksheet = XLSX.utils.json_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Monthly Staff Attendance Report",
      )
      const monthStr = selectedMonth.substring(0, 7)
      XLSX.writeFile(workbook, `staff_attendance_report_${monthStr}.xlsx`)
      toast.success("Exported to Excel successfully")
    } catch (error) {
      console.error("Error exporting to Excel:", error)
      toast.error("Failed to export to Excel")
    } finally {
      setExcelLoading(false)
    }
  }

  useEffect(() => {
    fetchEmployeeReport()
  }, [appliedFilters, selectedMonth, searchText])

  const renderTitle = (title1: string, title2: string) => (
    <div className="text-center">
      <span className="day-num d-block">{title1}</span>
      <span>{title2}</span>
    </div>
  )

  const generateDayColumns = (): ColumnsType<TableRowData> => {
    const dayColumns: ColumnsType<TableRowData> = []
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate()
    const dayNames = ["S", "M", "T", "W", "T", "F", "S"]
    const todayStr = getTodayStr()

    for (let day = 1; day <= daysInMonth; day++) {
      const dayNum = day
      const date = new Date(currentYear, currentMonth - 1, day)
      const dayName = dayNames[date.getDay()]
      const isFutureDate = date.toISOString().split("T")[0] > todayStr

      dayColumns.push({
        title: () => renderTitle(day.toString().padStart(2, "0"), dayName),
        dataIndex: `d${day.toString().padStart(2, "0")}`,
        key: `day_${day}`,
        render: (text: string, record: TableRowData) => {
          if (isFutureDate) {
            return (
              <div className="d-flex flex-column align-items-center gap-1">
                <span
                  className={`attendance-range ${date.getDay() === 0 ? "bg-secondary" : "bg-light"}`}
                  title={date.getDay() === 0 ? "Off Day" : "Not Marked"}
                ></span>
              </div>
            )
          }

          const detail = record.dayDetails?.[dayNum]
          const inTime = formatTime(detail?.checkIn)
          const outTime = formatTime(detail?.checkOut)

          if (inTime || outTime) {
            const bgColor = getTagBackgroundColor(detail?.status || text)
            const textColor = getTagTextColor(detail?.status || text)
            return (
              <div className="d-flex flex-column align-items-center gap-1">
                {inTime && (
                  <span
                    className="badge"
                    style={{
                      backgroundColor: bgColor,
                      color: textColor,
                      fontSize: "10px",
                      padding: "2px 6px",
                      borderRadius: "12px",
                      fontWeight: "500",
                      whiteSpace: "nowrap",
                    }}
                  >
                    IN: {inTime}
                  </span>
                )}
                {outTime && (
                  <span
                    className="badge"
                    style={{
                      backgroundColor: bgColor,
                      color: textColor,
                      fontSize: "10px",
                      padding: "2px 6px",
                      borderRadius: "12px",
                      fontWeight: "500",
                      whiteSpace: "nowrap",
                    }}
                  >
                    OUT: {outTime}
                  </span>
                )}
              </div>
            )
          }

          const normalized = normalizeStatus(text)
          const dotColor =
            STATUS_COLORS[normalized] ?? STATUS_COLORS["not-marked"]
          const statusText = STATUS_LABEL[normalized] ?? "Not Marked"

          return (
            <div className="d-flex flex-column align-items-center gap-1">
              <span
                className="attendance-range"
                style={{ backgroundColor: dotColor }}
                title={statusText}
              />
            </div>
          )
        },
      })
    }
    return dayColumns
  }

  const columns: ColumnsType<TableRowData> = [
    {
      title: "Staff",
      dataIndex: "originalData",
      key: "staff",
      render: (originalData: any) => {
        const user = originalData?.user
        const staffName = user
          ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
          : "Unknown Staff"
        return (
          <div className="d-flex align-items-center">
            <Link to="#" className="avatar avatar-md">
              <ImageWithBasePath
                src={"assets/img/profiles/avatar-01.jpg"}
                fullSrc={user?.profilePicture}
                className="img-fluid rounded-circle"
                alt="img"
              />
            </Link>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <Link
                  to={routes.staffAttendanceDetails.replace(
                    ":employeeId",
                    originalData?._id,
                  )}
                >
                  {staffName}
                </Link>
              </p>
              <small className="text-muted">
                {originalData?.employeeID || "No ID"}
              </small>
            </div>
          </div>
        )
      },
      sorter: (a: TableRowData, b: TableRowData) => {
        const nameA =
          `${a.originalData?.user?.firstName || ""} ${a.originalData?.user?.lastName || ""}`.toLowerCase()
        const nameB =
          `${b.originalData?.user?.firstName || ""} ${b.originalData?.user?.lastName || ""}`.toLowerCase()
        return nameA.localeCompare(nameB)
      },
      fixed: "left" as const,
      width: 200,
    },
    {
      title: "%",
      dataIndex: "percentageDisplay",
      key: "percentage",
      render: (text: string, record: TableRowData) => (
        <span className={record.percentClass}>{text}</span>
      ),
      sorter: (a: TableRowData, b: TableRowData) => a.percentage - b.percentage,
      width: 80,
    },
    {
      // Working days -- the denominator the % is calculated against. Days the
      // campus was closed (weekends, holidays, School Opening CLOSED days) are
      // excluded from it, so this is NOT simply the number of days in the month.
      title: "W",
      dataIndex: "w",
      key: "w",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.w - b.w,
    },
    {
      title: "P",
      dataIndex: "p",
      key: "p",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.p - b.p,
    },
    {
      title: "L",
      dataIndex: "l",
      key: "l",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.l - b.l,
    },
    {
      title: "A",
      dataIndex: "a",
      key: "a",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.a - b.a,
    },
    {
      title: "H",
      dataIndex: "h",
      key: "h",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.h - b.h,
    },
    {
      title: "Lv",
      dataIndex: "lv",
      key: "lv",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.lv - b.lv,
      render: (lv: number) => <span className="text-info">{lv}</span>,
    },
    ...generateDayColumns(),
  ]

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Staff Attendance Report</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Report</Link>
                  </li>
                  <li className="breadcrumb-item active">Attendance Report</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={handleRefresh}
                onPrint={handlePrintClick}
                onExportPdf={handleExportPdf}
                onExportExcel={handleExportExcel}
                onPrintBlank={handlePrintBlank}
                printLoading={printLoading}
                exportPdfLoading={pdfLoading}
                exportExcelLoading={excelLoading}
              />
            </div>
          </div>

          <div className="filter-wrapper">
            <div className="list-tab">
              <ul>
                <li>
                  <Link to={routes.attendanceReport}>Student Report</Link>
                </li>
                <li>
                  <Link to={routes.staffReport} className="active">
                    Staff Report
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="attendance-types page-header justify-content-end">
            <ul className="attendance-type-list">
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS.present }}
                >
                  <i className="ti ti-checks" />
                </span>{" "}
                Present
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS.absent }}
                >
                  <i className="ti ti-x" />
                </span>{" "}
                Absent
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS.late }}
                >
                  <i className="ti ti-clock-x" />
                </span>{" "}
                Late
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS["half-day"] }}
                >
                  <i className="ti ti-calendar-event" />
                </span>{" "}
                Half Day
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS.leave }}
                >
                  <i className="ti ti-calendar" />
                </span>{" "}
                Leave
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS["off-day"] }}
                >
                  <i className="ti ti-calendar-off" />
                </span>{" "}
                Off Day
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS["not-marked"] }}
                >
                  <i className="ti ti-clock-up" />
                </span>{" "}
                Not Marked
              </li>
            </ul>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                Staff Attendance Report - {selectedMonth}
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="d-flex flex-wrap me-2">
                  <input
                    type="month"
                    className="form-control mb-3"
                    value={selectedMonth}
                    onChange={e => handleMonthChange(e.target.value)}
                  />
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" /> Sort by{" "}
                    {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {CLASSES_SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${item.label === sortBy.label ? "active" : ""}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                dataSource={tableData}
                columns={columns}
                Selection={false}
                setSearchText={setSearchText}
                pagination={{
                  currentPage: paginationData.currentPage,
                  limit: paginationData.limit,
                  total: paginationData.total,
                  onChange: handlePaginationChange,
                }}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default StaffReport
